import React, { MutableRefObject, ReactElement, useEffect, useState } from 'react';

import { isSome } from 'fp-ts/lib/Option';

import Widget, { WidgetType } from 'domain/entity/widget';
import Branding from 'domain/entity/branding';
import { ConstraintsPerLayoutElement, LayoutType } from 'domain/entity/layout';
import WidgetWrapper from 'adapter/in/dashboards/pages/Widget/Wrapper';
import { useWidgetBuilder } from 'adapter/in/common/widget/widgetHooks';
import { findAvailableWidgetTypes, useFeatures } from 'adapter/in/common/context/Features/data';
import { AUTO_REFRESH_TIME } from 'adapter/in/dashboards/hooks/Dashboard/useAutorefreshDashboard';
import {
    dispatchCurrentWidgetConfiguration,
    KeyAndNamePair,
    useCurrentWidgetConfiguration,
} from 'adapter/in/common/context/currentWidgetConfiguration';
import { ReadWidgetDataCriteriaBuilder, WidgetDataProxyContext } from 'use-case/widgets/data/read/readWidgetDataCriteria';

export const useDashboardsWidgetBuilder = (
    widget: Widget,
    isEditEnabled: boolean,
    widgetDataContext?: WidgetDataProxyContext
): {
    WidgetBuilder: (
        width: number,
        height: number,
        layoutType?: LayoutType,
        isOnEdit?: boolean,
        sizeIssue?: ConstraintsPerLayoutElement
    ) => ReactElement;
    isSelectedWidget: boolean;
    branding: Branding;
    hasDataError: boolean;
    newPreset: KeyAndNamePair;
    newFeature: KeyAndNamePair;
} => {
    const widgetDataCriteria = (widgetId: string, widgetDataContext?: WidgetDataProxyContext) =>
        widgetDataContext !== undefined
            ? ReadWidgetDataCriteriaBuilder.create()
                  .withWidgetId(widgetId)
                  .withReportTemplateProxyContext(widgetDataContext)
                  .withDestination(widget.type === WidgetType.ANALYSIS ? 'analyses' : 'widgets')
                  .build()
            : ReadWidgetDataCriteriaBuilder.create()
                  .withWidgetId(widgetId)
                  .withDestination(widget.type === WidgetType.ANALYSIS ? 'analyses' : 'widgets')
                  .build();

    const autoRefresh = (lastTimeUpdated: MutableRefObject<number>) => lastTimeUpdated.current + AUTO_REFRESH_TIME < new Date().getTime();

    const { WidgetBuilder, hasDataError, branding } = useWidgetBuilder(
        widget,
        widgetDataCriteria,
        autoRefresh,
        undefined,
        widgetDataContext
    );

    const { widgetId: selectedWidgetId } = useCurrentWidgetConfiguration();
    const { setAvailableWidgetTypes } = dispatchCurrentWidgetConfiguration();
    const { readResult } = useFeatures();
    const [newPreset, setNewPreset] = useState<KeyAndNamePair>({ key: 'NONE', name: 'NONE' });
    const [newFeature, setNewFeature] = useState<KeyAndNamePair>({ key: 'NONE', name: 'NONE' });

    useEffect(() => {
        if (isEditEnabled) {
            if (isSome(readResult)) {
                const foundFeature = readResult.value.find((feature) => feature.presets.find((preset) => preset.key === widget.presetKey));
                const foundPreset = foundFeature?.presets.find((preset) => preset.key === widget.presetKey);

                if (foundFeature && foundPreset) {
                    setNewFeature({ key: foundFeature.key, name: foundFeature.name });
                    setNewPreset({ key: foundPreset.key, name: foundPreset.name });
                } else {
                    setNewFeature({ key: 'NONE', name: 'NONE' });
                    setNewPreset({ key: 'NONE', name: 'NONE' });
                }
            }
        }
    }, [readResult, widget, isEditEnabled]);

    useEffect(() => {
        if (isEditEnabled && isSome(readResult) && selectedWidgetId === widget.id) {
            setAvailableWidgetTypes(findAvailableWidgetTypes(widget.presetKey, readResult.value));
        }
    }, [selectedWidgetId, readResult, isEditEnabled]);

    return {
        WidgetBuilder,
        hasDataError,
        newPreset,
        newFeature,
        isSelectedWidget: selectedWidgetId === widget.id,
        branding,
    };
};

export type WidgetContainerProps = {
    dashboardId: string;
    widget: Widget;
    isEditingLayout: boolean;
    isEditEnabled: boolean;
    layoutType: LayoutType;
    alertTooltip?: string;
    sizeIssue?: ConstraintsPerLayoutElement;
    widgetDataContext?: WidgetDataProxyContext;
};

export const WidgetContainer = (props: WidgetContainerProps): ReactElement => {
    const { dashboardId, widget, isEditEnabled, isEditingLayout, layoutType, sizeIssue, alertTooltip, widgetDataContext } = props;
    const { WidgetBuilder, isSelectedWidget, hasDataError, branding, newFeature, newPreset } = useDashboardsWidgetBuilder(
        widget,
        isEditEnabled,
        widgetDataContext
    );

    return (
        <WidgetWrapper
            isSelectedWidget={isSelectedWidget}
            hasWidgetDataError={hasDataError}
            widgetType={widget.type}
            widgetId={widget.id}
            dashboardId={dashboardId}
            WidgetBuilder={WidgetBuilder}
            isEditingLayout={isEditingLayout}
            isEditEnabled={isEditEnabled}
            branding={branding}
            layoutType={layoutType}
            newPreset={newPreset}
            newFeature={newFeature}
            sizeIssue={sizeIssue}
            alertTooltip={alertTooltip}
        />
    );
};

export default WidgetContainer;
